import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const WHITE_LOGO = "/images/logos/logo-white.png";
const BLACK_LOGO = "/images/logos/logo-black.png";

const PrivacyPolicy = () => {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-white text-gray-900'}`}>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header with Logo */}
        <header className="mb-12">
          <Link to="/" className="inline-block mb-8">
            <img src={isDark ? WHITE_LOGO : BLACK_LOGO} alt="BoxCrowd" className="h-10 w-auto" />
          </Link>
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Last Updated: April 2025</p>
        </header>

        {/* Content */}
        <div className={`space-y-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Introduction</h2>
            <p className="leading-relaxed">
              BoxCrowd ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our holding page and join our waitlist.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Information We Collect</h2>
            <p className="leading-relaxed mb-3">
              When you join our waitlist, we collect:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your email address</li>
              <li>Timestamp of when you joined the waitlist</li>
              <li>Basic analytics data (page views, browser type, device information)</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>How We Use Your Information</h2>
            <p className="leading-relaxed mb-3">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Notify you when BoxCrowd launches</li>
              <li>Send you important updates about our platform</li>
              <li>Improve our website and services</li>
              <li>Analyze website traffic and user behavior</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Data Storage and Security</h2>
            <p className="leading-relaxed">
              Your email address is securely stored via EmailOctopus, our email service provider. We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Third-Party Services</h2>
            <p className="leading-relaxed mb-3">
              We use the following third-party services:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>EmailOctopus:</strong> For managing our waitlist and email communications</li>
              <li><strong>Google Analytics:</strong> For website analytics and performance tracking</li>
            </ul>
            <p className="leading-relaxed mt-3">
              These services may collect and process data according to their own privacy policies.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Your Rights (GDPR)</h2>
            <p className="leading-relaxed mb-3">
              Under GDPR, you have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Unsubscribe from our emails at any time</li>
              <li>Object to processing of your data</li>
              <li>Data portability</li>
            </ul>
            <p className="leading-relaxed mt-3">
              To exercise any of these rights, please contact us at <a href="mailto:contact@boxcrowd.com" className="text-[#00D4AA] hover:underline">contact@boxcrowd.com</a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Cookies</h2>
            <p className="leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last Updated" date at the top of this page.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-3">
              <a href="mailto:contact@boxcrowd.com" className="text-[#00D4AA] hover:underline font-semibold">contact@boxcrowd.com</a>
            </p>
          </section>
        </div>

        {/* Back to Home Link */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
          <Link to="/" className="text-[#00D4AA] hover:underline inline-flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
